/**
 * CallPage
 *
 * Thin route handler that delegates call setup to ActiveCallContext.
 * When navigated to with outgoing call state, it starts the call in the
 * persistent overlay and then navigates back so the user stays on
 * whatever page they came from (with the call running in the overlay).
 *
 * Incoming calls are handled entirely by ActiveCallContext (via call.ring
 * events) and never route here.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useActiveCall } from "@/features/call/context/ActiveCallContext";

export const CallPage = () => {
  const { t } = useTranslation();
  const { callId } = useParams<{ callId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { startCall, call: activeCall, showToast } = useActiveCall();
  const startedRef = useRef(false);

  const state = location.state as {
    otherUserId?: string;
    otherUserName?: string;
    otherUserImage?: string;
    otherUserCompany?: string;
    otherUserBatchNo?: string;
    otherUserVisaType?: string;
    otherUserType?: string;
    channelId?: string;
    isIncoming?: boolean;
  } | null;

  useEffect(() => {
    // Avoid double-fire in React StrictMode
    if (startedRef.current) return;

    // Incoming calls are handled by ActiveCallContext's ring listener
    if (state?.isIncoming) {
      navigate(-1);
      return;
    }

    // If there's already an active call, don't start another
    if (activeCall) {
      navigate(-1);
      return;
    }

    if (!callId || !state?.otherUserId) {
      navigate(-1);
      return;
    }

    startedRef.current = true;

    startCall(
      callId,
      {
        userId: state.otherUserId,
        name: state.otherUserName || "User",
        image: state.otherUserImage,
        company: state.otherUserCompany,
        batchNo: state.otherUserBatchNo,
        visaType: state.otherUserVisaType,
        userType: state.otherUserType,
      },
      state.channelId,
    )
      .then(() => {
        // Call is now active in the overlay — go back to previous page
        navigate(-1);
      })
      .catch((err) => {
        console.error("[CallPage] Failed to start call:", err);
        showToast(
          t("call.start_call_failed", { error: err.message || "Unknown error" }),
          "error",
        );
        navigate(-1);
      });
  }, [callId, state, activeCall, startCall, navigate, showToast, t]);

  // Show a brief loading state while the call is being set up
  return (
    <div className="h-full flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">{t("call.starting_call")}</p>
      </div>
    </div>
  );
};
